import { getPlugin } from "@/plugins";
import router from "@/router";
import { post, confirm, success, warning, error, info, alert } from "@/utils";
import type { PopupPosition } from "@nutui/nutui/dist/types/__VUE/popup/types";

export type HttpResult = {
  success: boolean;
  message: string;
  data: any;
};

export type PostResult = {
  result?: HttpResult;
  error?: any;
};

export type CallAction = {
  actionName: string;
  actionParameters: any;
};

export type QueueInstruction = { key: string; callAction: CallAction };

export type InstructionSet = {
  updateControl: Record<string, Record<string, Record<string, any>>>;
  callAction: Record<string, Record<string, CallAction[]>>;
  queue: Record<string, QueueInstruction[]>;
};

export type Slot = {
  name: string;
  components: Component[];
};

export type Control = {
  type: ControlType;
  tag: string;
  props: Record<string, any>;
  innerText?: string;
  slots?: Record<string, Component[]>;
  events?: Record<string, string[]>;
  vModel?: boolean;
};

export enum ControlType {
  MpButton = "MpButton",
  MpTabs = "MpTabs",
  MpTabbar = "MpTabbar",
  MpTabbarPage = "MpTabbarPage",
  MpNavbar = "MpNavbar",
  MpGrid = "MpGrid",
  MpCellGroup = "MpCellGroup",
  MpCell = "MpCell",
  MpTextCell = "MpTextCell",
  MpForm = "MpForm",
  MpFormItem = "MpFormItem",
  MpEmpty = "MpEmpty",
  MpStaticImage = "MpStaticImage",
  MpImageUpload = "MpImageUpload",
  MpUploadedImage = "MpUploadedImage",
  MpAvatar = "MpAvatar",
  MpAvatarUpload = "MpAvatarUpload",
  MpTextInput = "MpTextInput",
  MpTextarea = "MpTextarea",
  MpNumberInput = "MpNumberInput",
  MpBaseData = "MpBaseData",
  MpPicker = "MpPicker",
  MpMap = "MpMap",
  MpIcon = "MpIcon",
  MpPopup = "MpPopup",
  MpDivider = "MpDivider",
  MpSelectBase = "MpSelectBase",
  MpInfiniteLoadingList = "MpInfiniteLoadingList",
  MpList = "MpList",
  MpSimpleList = "MpSimpleList",
  MpListText = "MpListText",
  MpListButton = "MpListButton",
  MpListImage = "MpListImage",
  MpListBaseData = "MpListBaseData",
  MpListTextInput = "MpListTextInput",
  MpListNumberInput = "MpListNumberInput",
  MpListCell = "MpListCell",
  MpListAvatar = "MpListAvatar",
  MpListIcon = "MpListIcon",
  MpListList = "MpListList",
  MpCascader = "MpCascader",
  MpUploadedImageSelect = "MpUploadedImageSelect",
  MpUploader = "MpUploader",
  MpUploadedImageMultiSelect = "MpUploadedImageMultiSelect",
}

export type Component = {
  uid: string;
  type: string;
  key: string;
  name: string;
  control: Control;
};

export type FormMetadata = {
  form: Component;
  billHead: Component;
  components: Component[];
};

export type ShowFormParameters = {
  pageId: string;
  serviceType: string;
  metadata: FormMetadata;
  pkValue?: any;
  round?: boolean;
  position?: PopupPosition;
  background?: string;
};

export class BaseProxy {
  props: Record<string, any>;
  methods: Record<string, (...args: any) => void>;
  constructor() {
    this.props = {};
    this.methods = {};
  }

  setProperties(props: Record<string, any>) {
    this.props = { ...this.props, ...props };
  }

  callActions(actions: CallAction[]) {
    actions.forEach((act) => {
      const method = this.methods[act.actionName];
      if (!method) return;
      method(...act.actionParameters);
    });
  }

  callAction(action: CallAction) {
    const method = this.methods[action.actionName];
    if (!method) return;
    method(...action.actionParameters);
  }

  setMethod(name: string, method: (...args: any) => void) {
    this.methods[name] = method;
    return this;
  }
}

export class ControlProxy extends BaseProxy {
  showParams: ShowFormParameters;
  key: string;
  control: Control;
  ctrlRef: any;
  tag: string;
  handlers: Record<string, (args: any) => Promise<void>>;

  constructor(
    showParams: ShowFormParameters,
    key: string,
    control: Control,
    ctrlRef: any
  ) {
    super();
    this.showParams = showParams;
    this.key = key;
    this.control = control;
    this.ctrlRef = ctrlRef;
    this.tag = control.tag;
    this.handlers = {};
    this.setProperties(control.props);
  }

  init() {
    if (this.control.events) {
      this.handlers = Object.keys(this.control.events).reduce<
        Record<string, (args: any) => Promise<void>>
      >((handlers, event) => {
        handlers[event] = async (args) => {
          const plugin = getPlugin(
            this.showParams.metadata.form.control.props.formPlugins
          );

          if (plugin) {
            plugin.BeforeInvokeEvent(this, this.key, event, args);
          }

          const res = await post(`${this.showParams.serviceType}/InvokeEvent`, {
            pageId: this.showParams.pageId,
            key: this.key,
            event,
            args,
          });

          if (plugin) {
            plugin.AfterInvokeEvent(this, this.key, event, args);
          }

          handlePostResult(res);
        };
        return handlers;
      }, {});
    }

    if (this.control.vModel) {
      this.handlers["update:modelValue"] = async (newValue) => {
        this.setProperties({ modelValue: newValue });
        this.ctrlRef.setFormModel(newValue);
      };
    }
  }
}

const showAlert = async (
  pageId: string,
  serviceType: string,
  args: {
    title: string;
    message: string;
  }
) => {
  alert(args.message, args.title, async () =>
    handlePostResult(
      await post(`${serviceType}/AlertCallback`, {
        pageId,
      })
    )
  );
};

export class PageProxy extends BaseProxy {
  showParams: ShowFormParameters;
  popups: ShowFormParameters[];

  constructor(showParams: ShowFormParameters) {
    super();
    this.showParams = showParams;
    this.popups = [];
  }

  async init() {
    this.setMethod("navigateTo", (args: string) =>
      router.push({ path: "/page", query: { args: encodeURIComponent(args) } })
    );
    this.setMethod("navigateToRoute", (route: string) =>
      router.push({ path: `/${route}` })
    );
    this.setMethod("redirectTo", (args: string) =>
      router.replace({
        path: "/page",
        query: { args: encodeURIComponent(args) },
      })
    );
    this.setMethod("navigateBack", (delta: number) => router.back());

    this.setMethod(
      "showConfirm",
      (args: {
        title: string;
        message: string;
        showCancelButton: boolean;
        showConfirmButton: boolean;
        cancelButtonText?: string;
        confirmButtonText?: string;
      }) => {
        const $this = this;
        confirm(
          args.message,
          args.title,
          args.showCancelButton,
          args.showConfirmButton,
          args.cancelButtonText,
          args.confirmButtonText,
          async () => {
            handlePostResult(
              await post(`${$this.showParams.serviceType}/ConfirmCallback`, {
                pageId: $this.showParams.pageId,
                isOK: true,
              })
            );
          },
          async () => {
            handlePostResult(
              await post(`${$this.showParams.serviceType}/ConfirmCallback`, {
                pageId: $this.showParams.pageId,
                isOK: false,
              })
            );
          }
        );
      }
    );

    this.setMethod("showInfo", (message: string) => info(message));
    this.setMethod("showSuccess", (message: string) => success(message));
    this.setMethod("showWarning", (message: string) => warning(message));
    this.setMethod("showError", (message: string) => error(message));
    this.setMethod("alertInfo", (title: string, message: string) =>
      showAlert(this.showParams.pageId, this.showParams.serviceType, {
        message,
        title,
      })
    );
    this.setMethod("alertSuccess", (title: string, message: string) =>
      showAlert(this.showParams.pageId, this.showParams.serviceType, {
        message,
        title,
      })
    );
    this.setMethod("alertWarning", (title: string, message: string) =>
      showAlert(this.showParams.pageId, this.showParams.serviceType, {
        message,
        title,
      })
    );
    this.setMethod("alertError", (title: string, message: string) =>
      showAlert(this.showParams.pageId, this.showParams.serviceType, {
        message,
        title,
      })
    );

    this.setMethod(
      "makePhoneCall",
      (phoneNumber: string) => (window.location.href = `tel:${phoneNumber}`)
    );

    this.setMethod("showPopupForm", (showParameters: ShowFormParameters) =>
      this.popups.push(showParameters)
    );

    this.setMethod("close", (parentPageId: string) => {
      getProxy<PageProxy>(parentPageId, "$Form")?.removePopupForm(
        this.showParams.pageId
      );
    });

    handlePostResult(
      await post(`${this.showParams.serviceType}/Load`, {
        pageId: this.showParams.pageId,
      })
    );
  }

  removePopupForm(pageId: string) {
    const index = this.popups.findIndex((p) => p.pageId === pageId);
    if (index < 0) return;
    this.popups.splice(index, 1);
  }

  async close(pageId: string) {
    handlePostResult(
      await post(`${this.showParams.serviceType}/ClosePage`, { pageId })
    );
  }
}

const mapProxy: Record<string, Record<string, ControlProxy | PageProxy>> = {};
const mapVars: Record<string, any> = {};

export const setVar = (name: string, key: string, value: any) => {
  let mapPageVars = mapVars[name];
  if (!mapPageVars) {
    mapPageVars = {};
    mapVars[name] = mapPageVars;
  }

  mapPageVars[key] = value;
};

export const getVar = (name: string, key: string) => {
  const mapPageVars = mapVars[name];
  if (!mapPageVars) return null;
  return mapPageVars[key];
};

export const removePageVars = (name: string) => {
  delete mapVars[name];
};

export const getProxy = <T extends ControlProxy | PageProxy>(
  pageId: string,
  key: string
) => {
  const mapPageProxy = mapProxy[pageId];
  if (!mapPageProxy) return null;
  return mapPageProxy[key] as T;
};

export const setProxy = (
  pageId: string,
  key: string,
  proxy: ControlProxy | PageProxy
) => {
  const mapPageProxy = (mapProxy[pageId] = mapProxy[pageId] || {});
  mapPageProxy[key] = proxy;
};

export const removePageProxy = (pageId: string) => {
  delete mapProxy[pageId];
};

export const execInstruction = (data: InstructionSet) => {
  Object.keys(data.updateControl).forEach((pageId) => {
    const dicCtrlProps = data.updateControl[pageId]!;
    Object.keys(dicCtrlProps).forEach((key) => {
      const proxy = getProxy(pageId, key);
      if (!proxy) return;
      proxy.setProperties(dicCtrlProps[key]!);
    });
  });

  Object.keys(data.callAction).forEach((pageId) => {
    const dicActions = data.callAction[pageId]!;
    Object.keys(dicActions).forEach((key) => {
      const proxy = getProxy(pageId, key);
      if (!proxy) return;
      proxy.callActions(dicActions[key]!);
    });
  });

  Object.keys(data.queue).forEach((pageId) => {
    const list = data.queue[pageId]!;
    for (let i = 0; i < list.length; i++) {
      const ins = list[i]!;
      const proxy = getProxy(pageId, ins.key);
      if (!proxy) continue;
      proxy.callAction(ins.callAction);
    }
  });
};

export const handlePostResult = (result: { success: boolean; data: any }) => {
  if (!result.success) return;
  const data = result.data as InstructionSet;
  if (!data) return;
  execInstruction(data);
};

export const setProxyMethods = (ctrlType: ControlType, proxy: BaseProxy) => {
  switch (ctrlType) {
    case ControlType.MpTabs:
      proxy.setMethod("setActive", (active) => {
        (<ControlProxy>proxy).ctrlRef.setActive(active);
      });
      break;
    case ControlType.MpMap:
      proxy.setMethod("includePoints", (args) => {
        (<ControlProxy>proxy).ctrlRef.includePoints(args);
      });
      break;
    case ControlType.MpPopup:
      proxy.setMethod("show", () => {
        (<ControlProxy>proxy).ctrlRef.show();
      });
      proxy.setMethod("hide", () => {
        (<ControlProxy>proxy).ctrlRef.hide();
      });
      break;
    case ControlType.MpList:
      proxy.setMethod("setData", (data) => {
        (<ControlProxy>proxy).ctrlRef.setData(data);
      });
      proxy.setMethod("setChildrenStyle", (rowIndex, key, style) => {
        (<ControlProxy>proxy).ctrlRef.setChildrenStyle(rowIndex, key, style);
      });
      proxy.setMethod("setChildrenProps", (rowIndex, key, props) => {
        (<ControlProxy>proxy).ctrlRef.setChildrenProps(rowIndex, key, props);
      });
      proxy.setMethod("clearChildrenStyles", () => {
        (<ControlProxy>proxy).ctrlRef.clearChildrenStyles();
      });
      proxy.setMethod("clearChildrenProps", () => {
        (<ControlProxy>proxy).ctrlRef.clearChildrenProps();
      });
      break;
    case ControlType.MpInfiniteLoadingList:
      proxy.setMethod("setChildrenStyle", (rowIndex, key, style) => {
        (<ControlProxy>proxy).ctrlRef.setChildrenStyle(rowIndex, key, style);
      });
      proxy.setMethod("setChildrenProps", (rowIndex, key, props) => {
        (<ControlProxy>proxy).ctrlRef.setChildrenProps(rowIndex, key, props);
      });
      proxy.setMethod("clearChildrenStyles", () => {
        (<ControlProxy>proxy).ctrlRef.clearChildrenStyles();
      });
      proxy.setMethod("clearChildrenProps", () => {
        (<ControlProxy>proxy).ctrlRef.clearChildrenProps();
      });
      proxy.setMethod("reload", () => {
        (<ControlProxy>proxy).ctrlRef.reload();
      });
      proxy.setMethod("setTotal", (total) => {
        (<ControlProxy>proxy).ctrlRef.setTotal(total);
      });
      proxy.setMethod("addRows", (rows) => {
        (<ControlProxy>proxy).ctrlRef.addRows(rows);
      });
      proxy.setMethod("setFilterRows", (filterRows) => {
        (<ControlProxy>proxy).ctrlRef.setFilterRows(filterRows);
      });
      break;
    case ControlType.MpSelectBase:
      proxy.setMethod("setBaseList", (data) => {
        (<ControlProxy>proxy).ctrlRef.setBaseList(data);
      });
      break;
    case ControlType.MpUploadedImageSelect:
      proxy.setMethod("select", (data) => {
        (<ControlProxy>proxy).ctrlRef.select(data);
      });
      break;
    case ControlType.MpUploader:
      proxy.setMethod("clearFiles", () => {
        (<ControlProxy>proxy).ctrlRef.clearFiles();
      });
      break;
    case ControlType.MpUploadedImageMultiSelect:
      proxy.setMethod("select", (data) => {
        (<ControlProxy>proxy).ctrlRef.select(data);
      });
      break;
  }
};
